import React, { useState, useEffect } from 'react';
import { 
  Compass, 
  Save, 
  Edit3, 
  Clock, 
  Upload, 
  X 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const FIXED_CARDS = [
  { 
    key: 'vision', 
    label: 'Vision', 
    defaultTitle: 'To be inspired by faith, to be strengthened with fortitude, and to be motivated to achieve fulfillment through lifelong learning',
    defaultDesc: 'Our vision guides every decision we make, ensuring that faith, courage, and purpose are at the heart of our educational journey.'
  },
  { 
    key: 'mission', 
    label: 'Mission', 
    defaultTitle: 'PHB Secondary School is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
    defaultDesc: 'We empower every student to become confident learners, compassionate leaders, and responsible global citizens through rigorous academics and character formation.'
  },
  { 
    key: 'core_values', 
    label: 'Core Values', 
    defaultTitle: 'Sekolah Permata Harapan Secondary is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
    defaultDesc: 'Our core values of integrity, excellence, compassion, and service define who we are and guide how we treat one another every day.'
  }
];

export default function AboutUsVisionMissionValuesForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    intro: '',
    cards: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [editingCardIndex, setEditingCardIndex] = useState(null);
  const [modalCardData, setModalCardData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path;
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
              map[String(mediaId)] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (typeof mediaId === 'string' && (mediaId.startsWith('http://') || mediaId.startsWith('https://') || mediaId.startsWith('blob:') || mediaId.startsWith('data:'))) {
      return mediaId;
    }
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let valuesSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          valuesSection = result.data.find(s => s.section_type === 'about-us-values-shell');
        }
      }
      
      if (!valuesSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            valuesSection = result.data.find(s => s.section_type === 'about-us-values-shell');
          }
        }
      }
      
      if (!valuesSection) {
        throw new Error('Vision-Mission-Values section not found');
      }

      const currentSectionId = valuesSection.id;
      setSectionId(currentSectionId);

      let published = valuesSection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }

      const publishedEn = published?.en || {};
      const initialCards = FIXED_CARDS.map((fixed, idx) => {
        const existing = (Array.isArray(publishedEn.cards) && publishedEn.cards[idx]) || {};
        return {
          key: fixed.key,
          label: fixed.label,
          title: existing.title || fixed.defaultTitle,
          description: existing.description || fixed.defaultDesc,
          image: existing.image || '',
          media_id: existing.media_id || ''
        };
      });

      const initialData = {
        kicker: publishedEn.kicker || 'Our Direction',
        title: publishedEn.title || 'Vision, Mission, and Core Values',
        intro: publishedEn.intro || 'These guiding principles shape how we teach, lead, and cares.',
        cards: initialCards
      };

      const pendingMap = {};

      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${currentSectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                const cardMatch = change.field_id.match(/^cards\.(\d+)(?:\.|$)/);
                if (cardMatch) {
                  const cardIdx = parseInt(cardMatch[1], 10);
                  pendingMap[`card_${cardIdx}`] = true;
                  if (val && initialData.cards[cardIdx]) {
                    if (typeof val === 'object' && val !== null) {
                      delete val.update;
                    }
                    initialData.cards[cardIdx] = {
                      ...initialData.cards[cardIdx],
                      ...val
                    };
                  }
                } else if (change.field_id in initialData) {
                  pendingMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingMap);
    } catch (err) {
      console.error('[AboutUsVMVForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleModalCardChange = (field, value) => {
    setModalCardData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              const mediaUrl = response.url || response.file_path || response.data?.url || response.data?.file_path;
              if (mediaId) {
                if (mediaUrl) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: mediaUrl,
                    [String(mediaId)]: mediaUrl
                  }));
                }
                handleModalCardChange('image', mediaUrl || mediaId);
                handleModalCardChange('media_id', mediaId);
                toast.success('Pillar image uploaded! Click Save below to submit for approval.');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-values-shell`);
      xhr.withCredentials = true;
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (savedToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${savedToken}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: label || fieldName.replace(/_/g, ' '),
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitCardModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const cardIndex = editingCardIndex;
    const card = modalCardData;
    const cardKey = `card_${cardIndex}`;

    if (!card || !card.title || !card.description) {
      toast.warning('Please enter both title and description');
      return;
    }

    setSubmitting(prev => ({ ...prev, [cardKey]: true }));

    try {
      const fixed = FIXED_CARDS[cardIndex];
      const cardLabel = fixed?.label || `Card ${cardIndex + 1}`;
      
      const cleanCard = {
        key: fixed?.key || card.key || `card-${cardIndex}`,
        label: fixed?.label || card.label || cardLabel,
        title: (card.title || '').trim(),
        description: (card.description || '').trim(),
        image: card.image || '',
        media_id: card.media_id || ''
      };

      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `cards.${cardIndex}.update`,
            field_name: `Update ${cardLabel}`,
            old_value: originalData.cards[cardIndex] || {},
            new_value: cleanCard,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${cardLabel} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [cardKey]: true }));
        setFormData(prev => ({
          ...prev,
          cards: prev.cards.map((c, i) => i === cardIndex ? cleanCard : c)
        }));
        setEditingCardIndex(null);
        setModalCardData(null);
      } else {
        if (response.status === 409) {
          toast.error('This card already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [cardKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Vision, Mission & Values...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Compass size={22} />
          </div>
          <div>
            <h2 className="unified-title">Vision, Mission & Core Values</h2>
            <p className="unified-subtitle">Manage foundational principles and core pillars guiding the school community</p>
          </div>
        </div>
      </div>

      {/* Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="60"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Our Guiding Principles"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', 'Section Kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Headline</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="120"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Vision, Mission & Core Values"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Section Headline')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Intro */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Introduction</label>
          {pendingFields.intro && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="500"
            className="unified-textarea"
            rows="3"
            value={formData.intro}
            onChange={(e) => handleFieldChange('intro', e.target.value)}
            disabled={pendingFields.intro || submitting.intro}
            placeholder="A compelling introductory statement on how our values shape learning and daily student life..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('intro', 'Section Introduction')}
            disabled={submitting.intro || pendingFields.intro || formData.intro === originalData.intro}
          >
            <Save size={14} /> {submitting.intro ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Cards List (Vertical downward stack) */}
      <div style={{ marginTop: '34px' }}>
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            Foundational Cards (Vision, Mission & Core Values)
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
            Click edit on any card below to revise its headline, narrative, and optional badge icon
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {FIXED_CARDS.map((fixedCard, idx) => {
            const card = formData.cards[idx] || {};
            const cardKey = `card_${idx}`;
            const isPending = pendingFields[cardKey];
            const imgSrc = getMediaUrl(card.image);

            return (
              <div
                key={fixedCard.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '20px 24px',
                  borderRadius: '14px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderLeft: isPending ? '5px solid #f59e0b' : `5px solid ${idx === 0 ? '#2563eb' : idx === 1 ? '#6366f1' : '#0d9488'}`,
                  background: isPending ? '#fffdf7' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Optional Card Image / Icon */}
                {imgSrc && (
                  <div
                    style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      background: '#0f172a',
                      position: 'relative'
                    }}
                  >
                    <img
                      src={imgSrc}
                      alt={fixedCard.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setLightboxImage({ src: imgSrc, title: fixedCard.label })}
                    />
                  </div>
                )}

                {/* Card Content Summary */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: '6px',
                        background: '#eff6ff',
                        color: '#1d4ed8',
                        fontSize: '11px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em'
                      }}
                    >
                      {fixedCard.label}
                    </span>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                      {card.title || fixedCard.defaultTitle}
                    </h4>
                    {isPending && (
                      <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Clock size={10} /> Pending Review
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#475569',
                      lineHeight: 1.5,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {card.description || 'No description entered yet. Click Edit to add content.'}
                  </p>
                </div>

                {/* Action */}
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={() => {
                    if (isPending) {
                      toast.info('This card is currently awaiting approval');
                      return;
                    }
                    setEditingCardIndex(idx);
                    setModalCardData({ ...card });
                  }}
                  disabled={isPending}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                >
                  <Edit3 size={14} /> Edit Card
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Card Modal */}
      {editingCardIndex !== null && modalCardData && (
        <div className="unified-modal-overlay" onClick={() => {
          setEditingCardIndex(null);
          setModalCardData(null);
        }}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  Edit {FIXED_CARDS[editingCardIndex]?.label}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Update headline and description for this foundational pillar
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingCardIndex(null);
                  setModalCardData(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Title Input */}
              <div style={{ marginBottom: '18px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Pillar Headline</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalCardData.title || ''}
                  onChange={(e) => handleModalCardChange('title', e.target.value)}
                  placeholder={`e.g. ${FIXED_CARDS[editingCardIndex]?.defaultTitle}`}
                />
              </div>

              {/* Description Input */}
              <div style={{ marginBottom: '20px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Detailed Statement</label>
                <textarea
                  className="unified-textarea"
                  rows="5"
                  value={modalCardData.description || ''}
                  onChange={(e) => handleModalCardChange('description', e.target.value)}
                  placeholder="Elaborate on the vision, mission goals, or core behavioral values..."
                />
              </div>

              {/* Card Photo / Icon */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Pillar Image / Illustration</label>
                
                {modalCardData.image && getMediaUrl(modalCardData.image) && (
                  <div style={{ width: '120px', height: '80px', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px', border: '1px solid #cbd5e1' }}>
                    <img
                      src={getMediaUrl(modalCardData.image)}
                      alt="Pillar preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                )}

                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="file"
                    id="card-image-upload"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  />
                  <label htmlFor="card-image-upload" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                    <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : (modalCardData.image ? 'Replace Image' : 'Upload Image')}
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => {
                  setEditingCardIndex(null);
                  setModalCardData(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitCardModal}
                disabled={submitting[`card_${editingCardIndex}`] || !modalCardData.title || !modalCardData.description || imageUploading}
              >
                <Save size={14} /> {submitting[`card_${editingCardIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Pillar photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
